import { batch } from 'react-redux';
import { get, orderBy } from 'lodash';
import template from 'url-template';

import {
    FETCH_BAS_TYPES,
    PROFILE_REMOVE,
    PROFILE_REORDER,
    PROFILE_TOGGLE_SELECT,
    PROFILE_UPDATE,
    FETCH_PROFILES,
    SET_PROFILE
} from './actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';

import awsHeader from '../helpers/awsAuth';
import { getSortOrder } from '../helpers/collections';
import { promiseSerial } from '../helpers/promises';
import k2aBaseApi from '../apis/key2act';

import { basTypes } from '../data/basTypes';

const ProfileUrl = template.parse('/basprofiles/{profileId}');
const CompanyProfilesUrl = template.parse(
    '/companies/{companyId}/basprofiles{?limit,page,searchFilter,sessionId,sort}'
);

const DEFAULT_PROFILES_LIMIT = 1000;

export const createProfile = (companyId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            CompanyProfilesUrl.expand({ companyId }),
            body,
            header
        );

        const payload = get(response, 'data', []);

        batch(() => {
            dispatch({
                type:    PROFILE_UPDATE,
                payload: {
                    id:    payload.id,
                    patch: payload
                }
            });
            dispatch(setProfile(payload));
        });

        return payload;

    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchProfiles = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { auth: { instanceId: sessionId } } = getState();

    options.limit || (options.limit = DEFAULT_PROFILES_LIMIT);

    try {
        const response = await k2aBaseApi.get(
            CompanyProfilesUrl.expand({
                companyId,
                sessionId,
                ...options
            }),
            header
        );
        let payload = get(response.data, 'data', []);
        payload = orderBy(payload, [item => item.name.toUpperCase()], ['asc']);
        dispatch({
            type: FETCH_PROFILES,
            payload
        });
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchBasTypes = () => ({
    type:    FETCH_BAS_TYPES,
    payload: basTypes
});

export const fetchProfile = (profileId) => async (dispatch, getState) => {
    const header = await awsHeader();
    try {
        const response = await k2aBaseApi.get(
            ProfileUrl.expand({
                profileId
            }),
            header
        );
        const profile = response.data;
        batch(() => {
            dispatch({
                type:    PROFILE_UPDATE,
                payload: {
                    id:    profileId,
                    patch: profile
                }
            });
            dispatch(setProfile(profile));
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const removeProfile = ({ id }) => ({
    type:    PROFILE_REMOVE,
    payload: {
        id
    }
});

export const deleteProfiles = (companyId, profiles) => async dispatch => {
    const { headers } = await awsHeader();
    try {
        const tasks = profiles.map(profile => async () => {
            await k2aBaseApi.delete(
                ProfileUrl.expand({
                    profileId: profile.id
                }),
                {
                    headers
                }
            );
            dispatch(removeProfile(profile));
        });
        await promiseSerial(tasks);
        dispatch(enqueueSnackbar('Profile(s) deleted successfully', 'success'));
        dispatch(fetchProfiles(companyId));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const putProfile = (profile) => async (dispatch, getState) => {
    const header = await awsHeader();
    try {
        await k2aBaseApi.put(
            ProfileUrl.expand({
                profileId: profile.id
            }),
            profile,
            header
        );
        await dispatch(updateProfile(profile.id, profile));
        dispatch(enqueueSnackbar('Profile updated!', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const reorderProfiles = (newOrderBy) => async (dispatch, getState) => {
    const currentState = getState().profile;
    const order = getSortOrder(newOrderBy, currentState);
    dispatch({
        type:    PROFILE_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const toggleSelectProfile = (id) => ({
    type:    PROFILE_TOGGLE_SELECT,
    payload: { id }
});

export const updateProfile = (id, patch) => ({
    type:    PROFILE_UPDATE,
    payload: {
        id,
        patch
    }
});

export const setProfile = profile => ({
    type:    SET_PROFILE,
    payload: profile
});
